import { Form, FormInstance, FormRule, Tooltip } from "antd";
import {
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import styles from "./style.module.scss";
import VLButton from "../../../../../uikit/Buttons/VLButton";
import { useEffect, useRef, useState } from "react";
import last from "lodash/last";
import SupportedFormats from "../../../../../uikit/FormFields/DragAndDropBase/SupportedFormats";
import { folderFormats } from "../../../../../uikit/FormFields/FoldersDropBox";
import { VLInput } from "../../../../../uikit/FormFields/VLInput/VLInput";
import classNames from "classnames";

export const FoldersPathsForm = ({
  form,
  onFinish,
  isValidating,
}: {
  isValidating: boolean;
  onFinish: (v: { paths: string[] }) => void;
  form: FormInstance;
}) => {
  // eslint-disable-next-line
  const paths: string[] = Form.useWatch("paths", form) || [];
  const ref = useRef<HTMLDivElement>(null);
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const [hasDuplicate, setHasDuplicate] = useState(false);
  useEffect(
    () => setHasDuplicate(new Set(paths).size !== paths.length),
    [paths],
  );
  useEffect(() => {
    const keyDown = (e: KeyboardEvent) => {
      if (e.code === "Enter") {
        e.preventDefault();
        const lastAddButton = last(
          ref.current!.getElementsByClassName(styles.addButton),
        ) as HTMLButtonElement;
        lastAddButton?.click();
      }
    };
    document.addEventListener("keydown", keyDown);
    return () => document.removeEventListener("keydown", keyDown);
  }, []);
  const isAddDisabled = paths.some((p) => !p) || hasDuplicate;
  return (
    <div ref={ref}>
      <Form
        className={styles.foldersPathsForm}
        id="folders-paths-form"
        form={form}
        onFinish={onFinish}
        initialValues={{ paths: [""] }}
      >
        <div className={styles.title}>
          Enter Folders Paths{" "}
          <Tooltip
            placement={"right"}
            title={<SupportedFormats formatList={folderFormats} />}
          >
            <InfoCircleOutlined className={styles.infoIcon} />
          </Tooltip>
        </div>
        <Form.List name="paths">
          {(fields, { add, remove }) =>
            fields.map((field, i) => {
              inputRefs.current = [];
              const handleRemove = () => remove(i);
              const handleAdd = () => {
                add("");
                requestAnimationFrame(() => {
                  inputRefs.current[inputRefs.current.length - 1]?.focus();
                });
              };
              return (
                <div className={styles.row}>
                  <Form.Item
                    {...field}
                    className={styles.inputField}
                    rules={createPathRules(paths)}
                  >
                    <VLInput
                      placeholder="(e.g., project1/images)"
                      size="large"
                      ref={(el) => {
                        if (el) {
                          inputRefs.current[i] = el.input!;
                        }
                      }}
                    />
                  </Form.Item>
                  {i === fields.length - 1 && (
                    <VLButton
                      className={classNames(styles.button, styles.addButton)}
                      type="default"
                      icon={<PlusOutlined />}
                      disabled={!paths[i] || hasDuplicate}
                      onClick={handleAdd}
                    />
                  )}
                  <VLButton
                    className={styles.button}
                    type="default"
                    icon={<DeleteOutlined className={styles.iconDelete} />}
                    disabled={i === 0}
                    onClick={handleRemove}
                  />
                </div>
              );
            })
          }
        </Form.List>
        <div className={styles.submitButtonWrapper}>
          <VLButton
            loading={isValidating}
            className={styles.submitButton}
            disabled={isAddDisabled}
            type="primary"
            text="Add"
            size="large"
            htmlType="submit"
          />
        </div>
      </Form>
    </div>
  );
};

const createPathRules = (paths: string[]): FormRule[] => [
  {
    pattern: new RegExp(
      '^(?![a-zA-Z]:[\\\\/])(?!\\\\\\\\)[^:*?"<>|]+(?:[\\\\/][^:*?"<>|]+)*$',
    ),
    message: "Only relative paths inside datasets are supported.",
  },
  {
    validator: (_: any, value: string) => {
      if (!value) return Promise.resolve();

      const duplicates = paths.filter((p) => p === value);
      if (duplicates.length > 1) {
        return Promise.reject("This folder has already been added.");
      }

      return Promise.resolve();
    },
  },
];
